/** The Claim Strategy stage: every read and write answers the stage whole, since moving a claim renumbers the rest. */

import { delReturning, get, patch, post, put } from "@/services/api-client";
import type {
  ClaimAddRequest,
  ClaimEditRequest,
  ClaimMarkerScheme,
  ClaimMoveRequest,
  ClaimOrderRequest,
  ClaimStrategyResponse,
  ProposalStateResponse,
} from "@patent-studio/api-types";

export function getClaimStrategy(
  taskId: string,
): Promise<ClaimStrategyResponse> {
  return get<ClaimStrategyResponse>(`/tasks/${taskId}/claim-strategy`);
}

/** Asks for a first claim set; it lands through the worker, and the stream says when. */
export function proposeClaimSet(
  taskId: string,
): Promise<ProposalStateResponse> {
  return post<ProposalStateResponse>(`/tasks/${taskId}/claim-strategy/propose`);
}

/** One change to one claim: its words, its width, or its placeholder mark. */
export function editClaim(
  taskId: string,
  claimId: string,
  body: ClaimEditRequest,
): Promise<ClaimStrategyResponse> {
  return patch<ClaimStrategyResponse>(
    `/tasks/${taskId}/claim-strategy/claims/${claimId}`,
    body,
  );
}

export function confirmClaim(
  taskId: string,
  claimId: string,
): Promise<ClaimStrategyResponse> {
  return post<ClaimStrategyResponse>(
    `/tasks/${taskId}/claim-strategy/claims/${claimId}/confirm`,
  );
}

export function unconfirmClaim(
  taskId: string,
  claimId: string,
): Promise<ClaimStrategyResponse> {
  return post<ClaimStrategyResponse>(
    `/tasks/${taskId}/claim-strategy/claims/${claimId}/unconfirm`,
  );
}

const stage = (taskId: string) => `/tasks/${taskId}/claim-strategy`;

/** A claim built from an agreed position when one is named, or an empty one. */
export function addClaim(
  taskId: string,
  body: ClaimAddRequest,
): Promise<ClaimStrategyResponse> {
  return post<ClaimStrategyResponse>(`${stage(taskId)}/claims`, body);
}

export function promoteClaim(
  taskId: string,
  claimId: string,
): Promise<ClaimStrategyResponse> {
  return post<ClaimStrategyResponse>(
    `${stage(taskId)}/claims/${claimId}/promote`,
  );
}

export function demoteClaim(
  taskId: string,
  claimId: string,
  parentClaimId: string,
): Promise<ClaimStrategyResponse> {
  return post<ClaimStrategyResponse>(
    `${stage(taskId)}/claims/${claimId}/demote`,
    {
      parent_claim_id: parentClaimId,
    },
  );
}

/** Tags the claim and its dependents with a category the set holds, or a new one made in the same write. */
export function moveClaim(
  taskId: string,
  claimId: string,
  body: ClaimMoveRequest,
): Promise<ClaimStrategyResponse> {
  return post<ClaimStrategyResponse>(
    `${stage(taskId)}/claims/${claimId}/move`,
    body,
  );
}

/** Answers the set that is left, renumbered; an independent claim takes its chain with it. */
export function removeClaim(
  taskId: string,
  claimId: string,
): Promise<ClaimStrategyResponse> {
  return delReturning<ClaimStrategyResponse>(
    `${stage(taskId)}/claims/${claimId}`,
  );
}

/** Answers the set with one form's independent claims, or one chain's dependents, in the new order. */
export function reorderClaims(
  taskId: string,
  body: ClaimOrderRequest,
): Promise<ClaimStrategyResponse> {
  return put<ClaimStrategyResponse>(`${stage(taskId)}/claims/order`, body);
}

/** Answers the set with every marker respelled; no claim's words move and no confirmation lapses. */
export function setMarkerScheme(
  taskId: string,
  scheme: ClaimMarkerScheme,
): Promise<ClaimStrategyResponse> {
  return put<ClaimStrategyResponse>(`${stage(taskId)}/marker-scheme`, {
    marker_scheme: scheme,
  });
}

/** Locks the set at the address it was read at; refused while a claim is unconfirmed or the set moved. */
export function lockClaimSet(
  taskId: string,
  address: string,
): Promise<ClaimStrategyResponse> {
  return post<ClaimStrategyResponse>(`${stage(taskId)}/lock`, { address });
}
